import { InfoCircleFilled } from '@ant-design/icons';
import { Alert, Flex } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { Link, Subtitle, Text } from 'components/mvp-typography';
import { useBillingPermissions } from 'features/clinic/billing/hooks/use-billing-permissions';
import RegistrationSteps from 'features/clinic/onboarding/registration/_components/RegistrationSteps';
import { useGetPrincipalUser } from 'lib/hooks/tanstack/use-getClinicInfo';
import { useHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { paths } from 'routes';
import { theme } from 'styles/colors';
import { useAuth } from 'lib/hooks/auth/use-auth';

export function OnboardingRequiredDialog() {
  const { currentClinic } = useClinicianContext();
  const hasPaymentMethodQuery = useHasPaymentMethod(currentClinic);
  const billingPermissions = useBillingPermissions();
  const principalUserQuery = useGetPrincipalUser(currentClinic);
  const { user } = useAuth();

  if (
    hasPaymentMethodQuery.isLoading ||
    billingPermissions.isLoading ||
    principalUserQuery.isLoading
  ) {
    return null;
  }

  const hasNoPaymentMethod =
    hasPaymentMethodQuery.data?.data.hasPaymentMethod === false;
  const isBothBalancesInfinite =
    billingPermissions.isBothBalancesInfinite === true;
  const principalUserStatus = principalUserQuery.data?.data.status;

  const isPrincipalUser =
    user?.uid && principalUserQuery.data?.data?.uid === user.uid;
  const principalUserEmail =
    principalUserQuery.data?.data?.email || 'the Principal User';

  /**
   * Do not show anything if there is no payment method, but both balances are infinite
   * Otherwise, do not show anything if no purchases are allowed (i.e: read only)
   */
  if (
    !hasNoPaymentMethod ||
    (hasNoPaymentMethod && isBothBalancesInfinite) ||
    billingPermissions.isBothBalancesPurchaseDisabled
  ) {
    return null;
  }

  const currentStep = principalUserStatus === 'pending' ? 2 : 3;

  const steps = [
    {
      title: 'Create your account'
    },
    {
      title: 'Create your organization'
    },
    {
      title: 'Confirm Principal User',
      description: (
        <div>
          {principalUserStatus === 'pending' && (
            <div>
              <Text>
                The current Principal User for this Organization is:{' '}
                <strong>
                  {principalUserQuery.data?.data.email ||
                    principalUserQuery.data?.data.uid ||
                    'Unknown'}
                </strong>
              </Text>
              <br />
              <Link
                href={paths.clinician.billingSettings()}
                style={{ textDecoration: 'none' }}
              >
                <BaseButton>Confirm Principal User</BaseButton>
              </Link>
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Set up billing',
      description: (
        <div style={{ marginTop: '4px' }}>
          <Text>
            Your organisation does not have a payment method set up. You will
            not be able to purchase new assessments until this is complete.
          </Text>
          <br />
          {currentStep === 3 && (
            isPrincipalUser ? (
              <Link
                href={paths.clinician.billingSettings()}
                style={{ textDecoration: 'none' }}
              >
                <BaseButton>Setup billing</BaseButton>
              </Link>
            ) : (
              <div>
                <BaseButton disabled>Setup billing</BaseButton>
                <br />
                <Text style={{ marginTop: '8px', display: 'block' }}>
                  Please contact the Principal User ({principalUserEmail}) to set up billing.
                </Text>
              </div>
            )
          )}
        </div>
      )
    }
  ];

  const remainingSteps = steps.length - currentStep;

  return (
    <Flex vertical align="center" justify="center">
      <Alert
        showIcon
        icon={<InfoCircleFilled style={{ color: theme.primary }} />}
        style={{
          width: '100%',
          maxWidth: 1200,
          backgroundColor: '#ffffff',
          borderColor: theme.primary
        }}
        message={
          <>
            <Subtitle style={{ fontWeight: 'bold' }}>
              Complete onboarding
            </Subtitle>
          </>
        }
        description={
          <Flex vertical gap={16} style={{ maxWidth: '50vw' }}>
            <Text>
              You have {remainingSteps} onboarding{' '}
              {remainingSteps === 1 ? 'step' : 'steps'} remaining before you
              unlock full functionality of the platform.
            </Text>
            <RegistrationSteps
              current={currentStep}
              stepsProps={{
                style: { marginTop: '8px' },
                direction: 'vertical',
                items: steps
              }}
            />
          </Flex>
        }
      />
    </Flex>
  );
}
